import { queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { QuoteBuilderApiError, type QuoteBuilderApi } from '../api/client'
import { retainNewerStageRead } from '../api/queries'
import type { Quote } from '../api/types'

type Schemas = components['schemas']

export type InstallationEquipment = Schemas['QbInstallationEquipmentRead']
export type InstallationEstimate = Schemas['QbInstallationEstimateRead']
export type InstallationPreview = Schemas['QbInstallationPreviewRead']

/** One editable decision as the stage keeps it: the decimal fields as typed strings. */
export type InstallationEstimateInput = {
  equipment_line_id: string
  rule_id: string
  included: boolean
  crew_hours: string
  hourly_rate: string
  quantity: string
  notes: string | null
  net_override: string | null
  markup_percent: string | null
}

export const servicesQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['quote-services', quoteId],
    queryFn: ({ signal }) =>
      api.request<InstallationPreview>(`/quotes/${quoteId}/services`, {
        signal,
      }),
    structuralSharing: retainNewerStageRead,
    enabled: Boolean(quoteId),
  })

export function draftEstimate(estimate: InstallationEstimate): InstallationEstimateInput {
  return {
    equipment_line_id: estimate.equipment_line_id,
    rule_id: estimate.rule_id,
    included: estimate.included,
    crew_hours: String(Number(estimate.crew_hours)),
    hourly_rate: String(Number(estimate.hourly_rate)),
    quantity: String(Number(estimate.quantity)),
    notes: estimate.notes || null,
    net_override: estimate.net_override == null ? null : String(Number(estimate.net_override)),
    markup_percent: estimate.markup_percent == null ? null : String(Number(estimate.markup_percent)),
  }
}

const fields = ['included', 'crew_hours', 'hourly_rate', 'quantity', 'notes', 'net_override', 'markup_percent'] as const
const labels = {
  included: 'installation choice',
  crew_hours: 'crew hours',
  hourly_rate: 'hourly rate',
  quantity: 'quantity',
  notes: 'notes',
  net_override: 'Net',
  markup_percent: 'markup',
}

/** Keep the user's edits over a newer server copy; take the server's value where they did not edit. */
export function mergeDrafts(
  current: InstallationEstimateInput[],
  previous: InstallationEstimateInput[],
  incoming: InstallationEstimateInput[],
) {
  return incoming.map((next) => {
    const draft = current.find((item) => item.equipment_line_id === next.equipment_line_id)
    const baseline = previous.find((item) => item.equipment_line_id === next.equipment_line_id)
    if (!draft || !baseline) return next
    return fields.reduce(
      (merged, field) => ({
        ...merged,
        [field]: draft[field] === baseline[field] ? next[field] : draft[field],
      }),
      { ...next },
    )
  })
}

export class ServicesConflictError extends Error {
  quote: Quote
  preview: InstallationPreview

  constructor(message: string, quote: Quote, preview: InstallationPreview) {
    super(message)
    this.name = 'ServicesConflictError'
    this.quote = quote
    this.preview = preview
  }
}

export function servicesError(error: unknown) {
  if (error instanceof ServicesConflictError) return error.message
  if (error instanceof QuoteBuilderApiError) {
    if (error.code === 'installation_rule_missing') {
      return 'An installation rate is missing. Ask an administrator to restore the installation rule, then refresh.'
    }
    if (error.code === 'project_already_started')
      return 'This quote is already a project, so services can no longer change.'
    if (error.status === 401 || error.status === 403) return 'Sign in again, then apply your retained decisions.'
    if (error.status === 422) {
      return 'Check hours are positive, quantity is a whole number, prices are zero or more, and markup is between -100% and 10,000%.'
    }
  }
  return 'Services could not be saved. Your edits are retained; check your connection and apply again.'
}

export function applyServicesRequest(
  api: QuoteBuilderApi,
  quote: Pick<Quote, 'id' | 'revision'>,
  estimates: InstallationEstimateInput[],
) {
  return api.request<Quote>(`/quotes/${quote.id}/services`, {
    method: 'PUT',
    body: JSON.stringify({ expected_revision: quote.revision, estimates }),
  })
}

/**
 * Save decisions, retrying a lost or stale save the way the demo does: reread the quote and the
 * calculator, carry the user's edits onto the newer copy, and stop with a conflict when someone
 * else changed the same field or the equipment changed underneath.
 */
export async function applyServices(
  api: QuoteBuilderApi,
  quote: Pick<Quote, 'id' | 'revision'>,
  submitted: InstallationEstimateInput[],
  baseline: InstallationEstimateInput[],
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<{ quote: Quote; submitted: InstallationEstimateInput[] }> {
  let current = quote
  for (let attempt = 0; ; attempt++) {
    try {
      return {
        quote: await applyServicesRequest(api, current, submitted),
        submitted,
      }
    } catch (error) {
      const recoverable =
        error instanceof TypeError ||
        (error instanceof QuoteBuilderApiError &&
          (error.status === 412 ||
            error.status === 408 ||
            error.status === 429 ||
            error.status >= 500 ||
            error.code === 'installation_estimate_changed'))
      if (!recoverable || attempt >= 3) throw error
      await wait(250 * 2 ** attempt)
      const [nextQuote, preview] = await Promise.all([
        api.request<Quote>(`/quotes/${current.id}`),
        api.request<InstallationPreview>(`/quotes/${current.id}/services`),
      ])
      const incoming = preview.estimates.map(draftEstimate)
      const changedSet =
        incoming.length !== baseline.length ||
        incoming.some(
          (item) =>
            !baseline.some(
              (before) => before.equipment_line_id === item.equipment_line_id && before.rule_id === item.rule_id,
            ),
        )
      if (changedSet || nextQuote.revision !== preview.revision) {
        throw new ServicesConflictError(
          'Equipment or installation rates changed. Review the updated rows and your retained edits, then apply.',
          nextQuote,
          preview,
        )
      }
      for (const next of incoming) {
        const before = baseline.find((item) => item.equipment_line_id === next.equipment_line_id)
        const draft = submitted.find((item) => item.equipment_line_id === next.equipment_line_id)
        if (!before || !draft) continue
        const conflicts = fields.filter(
          (field) => draft[field] !== before[field] && next[field] !== before[field] && draft[field] !== next[field],
        )
        if (conflicts.length) {
          const model = preview.estimates.find((item) => item.equipment_line_id === next.equipment_line_id)?.equipment
            .model
          throw new ServicesConflictError(
            `${model}: ${conflicts.map((field) => labels[field]).join(', ')} changed elsewhere. Review your retained values, then apply.`,
            nextQuote,
            preview,
          )
        }
      }
      submitted = mergeDrafts(submitted, baseline, incoming)
      baseline = incoming
      current = nextQuote
      // A lost response may follow a committed write. Read before repeating it.
      if (preview.estimates.every((item) => item.reviewed) && JSON.stringify(submitted) === JSON.stringify(incoming)) {
        return { quote: nextQuote, submitted }
      }
    }
  }
}
